import { ChallengeActivity, SearchIcon } from '../types';
import { DEFAULT_CHALLENGE_REWARD_ICON, ICONS8_KEY } from '../helpers/constants';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, isDefined, undefinedIfEmpty } from '../helpers/utils';
import { dupeChallengeActivitysExist, validateIcon } from '../helpers/tags-categories';
import { externalGet, logWithAmplitude, throwHttpError } from '../helpers/api/api';

import { Dialog } from '@material/mwc-dialog';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import { getMainPage } from '../helpers/page-name';
import topicData from '../helpers/state/topic-data';

@customElement('challenge-activity-form')
export class ChallengeActivityForm extends LitElement {
    amplitude_callsite: string = 'Challenge Activity Form';

    @query('#iconURL')
    private readonly iconUrl?: TextField;

    @query('#iconUrlSelector')
    private readonly iconUrlSelector!: Dialog;

    @query('#search')
    searchInput!: TextField;

    @property()
    allData: ChallengeActivity[] = [];

    @property()
    pickedIcon?: SearchIcon;

    @state()
    selectedIconUrl: string | undefined;

    @property({ type: Object })
    data?: ChallengeActivity;

    @state()
    ChallengeActivity?: ChallengeActivity;

    @property({ type: String })
    icon: string = DEFAULT_CHALLENGE_REWARD_ICON;

    @state()
    point_value: number = 1;

    @state()
    submission_type: string = 'photos';

    @state()
    max_redemptions: number = 1;

    @state()
    renderableIconString: string = this.icon;

    @state()
    rotatePreviewCSS: string = '';

    @state()
    iconSearchResults: any[] = [];

    @state()
    openTab: string = 'selectIconContent';

    private readonly bindTopicState = new StateController(this, topicData);

    updated(changedProperties: any) {
        changedProperties.forEach((oldValue: any, propName: any) => {
            if(propName === 'data') {
                this.ChallengeActivity = this.data;
                this.max_redemptions = this.ChallengeActivity?.max_redemptions ?? 1;
                this.point_value = this.ChallengeActivity?.point_value ?? 1;
                this.icon = this.ChallengeActivity?.image_url ?? DEFAULT_CHALLENGE_REWARD_ICON;
                this.point_value = this.ChallengeActivity?.point_value ?? 1;
                this.submission_type = this.ChallengeActivity?.submission_type ?? 'photos';
            }

            if (propName === 'icon') {
                this.renderableIconString = this.icon;
            }
        });
    }


    onChangeChallengeActivityTitle(e: CustomEvent) {
        const name = (e.target as HTMLInputElement).value;

        const dupeChallengeActivityName = dupeChallengeActivitysExist(this.allData, name);

        if (dupeChallengeActivityName) {
            getMainPage().errorToastElement.showToastAndThrow(`An activity named ${dupeChallengeActivityName} already exists. Please choose a different name.`);
        }

        if (isDefined(this.ChallengeActivity)) {
            this.ChallengeActivity.name = name;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.ChallengeActivity
                    }
                })
            );
        }
    }

    onChangeChallengeActivityDescription(e: CustomEvent) {
        const description = (e.target as HTMLInputElement).value;

        if (isDefined(this.ChallengeActivity)) {
            this.ChallengeActivity.description = description;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.ChallengeActivity
                    }
                })
            );
        }
    }

    private async searchForIconInternal() {
        const search = undefinedIfEmpty(this.searchInput?.value) ?? 'award';
        this.iconSearchResults = await this.findIcon(this.amplitude_callsite, search);
    }

    private  async findIcon(amplitude_callsite: string, term: string) {
    if (ICONS8_KEY) {
        const response = await fetch(
            `https://search.icons8.com/api/iconsets/v5/search?term=${term}&token=${ICONS8_KEY}`,
            externalGet()
        );
        if (!response.ok) {
            await throwHttpError('An error occurred while listing icons.', response);
        }

        const icons = (await response.json()).icons as SearchIcon[];
        logWithAmplitude(amplitude_callsite, 'Icon Search', { search_text: term });
        return icons;
    } else {
        throw new Error('ICONS8_KEY can not be found');
    }
}

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    private readonly searchForIcon = debounce(this.searchForIconInternal, 200);

    private async customIconSelected(url: string) {
        const validatedIcon = await validateIcon(url);
        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }
        this.icon = validatedIcon ?? DEFAULT_CHALLENGE_REWARD_ICON;

        if (isDefined(this.ChallengeActivity)) {
            this.ChallengeActivity.image_url = url;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.ChallengeActivity
                    }
                })
            );
        }

        logWithAmplitude(this.amplitude_callsite, 'Custom Icon Selected', {'icon_url':  url});
    }

    onChangeSubmissionType(e: Event) {
        const submission_type = (e.target as HTMLInputElement).value;
        this.submission_type = submission_type;

        if (isDefined(this.ChallengeActivity)) {
            this.ChallengeActivity.submission_type = submission_type as 'text' | 'photos' | 'url' | 'receipt';

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.ChallengeActivity
                    }
                })
            );
        }
    }

    onChangePointValue(e: Event) {
        const point_value = (e.target as HTMLInputElement).value;
        const point_value_number = point_value ? Number(point_value) : 1;

        this.point_value = point_value_number;
        if (isDefined(this.ChallengeActivity)) {
            this.ChallengeActivity.point_value = point_value_number;
            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    bubbles: true,
                    composed: true,
                    detail: {
                        data: this.ChallengeActivity
                    }
                })
            );
        }
    }

    private async changeOpenTab(tab: string) {
        this.openTab = tab;
        // if icon url tab is opened, focus on the input and search for icons
        if (tab === 'selectIconContent') {
            this.searchInput?.focus();
            await this.searchForIconInternal();
        }
    }

    private async clickIcon(icon: SearchIcon) {
        this.pickedIcon = icon;
        this.selectedIconUrl = `https://img.icons8.com/${icon.platform}/${icon.commonName}.png`;
        await this.customIconSelected(this.selectedIconUrl);
    }

    async onChangeIconUrl(e: CustomEvent) {
        if (e.detail?.imageUrl === undefined) {
            return;
        }
        const iconUrl = e.detail?.imageUrl;
        const validatedIcon = await validateIcon(iconUrl);

        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }

        this.rotatePreviewCSS = '';
        this.icon = validatedIcon ?? DEFAULT_CHALLENGE_REWARD_ICON;

        if (isDefined(this.ChallengeActivity)) {
            this.ChallengeActivity.image_url = iconUrl;

            this.dispatchEvent(
                new CustomEvent('on-change-value', {
                    detail: {
                        data: this.ChallengeActivity
                    }
                })
            );
        }

        logWithAmplitude(this.amplitude_callsite, 'Challenge Award Icon URL Changed', {'icon_url':  e.detail.imageUrl});
    }

    async onChangeImage(e: CustomEvent) {
        const imageToUpload = e.detail?.imageToUpload;
        this.rotatePreviewCSS =  e.detail?.rotatePreviewCSS;
        this.icon = URL.createObjectURL(imageToUpload?.file);

        this.dispatchEvent(new CustomEvent('on-change-reward-image', {
            detail: {
                imageToUpload: isDefined(imageToUpload)
                    ? imageToUpload
                    : undefined,
            },
        }));
    }

    cancelImageUpload() {
        this.rotatePreviewCSS = '';
        this.dispatchEvent(new CustomEvent('on-change-reward-image', {
            detail: {
                imageToUpload: undefined,
            },
        }));

        this.iconUrlSelector.close();
    }

    render() {
        return html`
            <span class="edit__inputs-container">
                <span class="edit__inputs">
                    <proxi-textfield outlined autofocus id="name" class="edit__textfield" label="Award Name" @change=${this.onChangeChallengeActivityTitle} value="${this.ChallengeActivity?.name ?? ''}" charcounter minlength="1" maxlength="35" required></proxi-textfield>
                    <proxi-textarea outlined id="description" class="edit__textfield" label="Award Message" @change=${this.onChangeChallengeActivityDescription} value="${this.ChallengeActivity?.description ?? ''}" charcounter maxlength="140"></proxi-textarea>
                    ${this.ChallengeActivity
                        ? html`
                            <br />


                            <div class="success-criteria">

                                <div class="value">
                                    <p class="slider-header">Point Value</p>
                                    <proxi-textfield
                                        outlined
                                        class="input reward-criteria-threshold"
                                        id="point_value"
                                        type="number"
                                        label=""
                                        placeholder=""
                                        value="${this.point_value}"
                                        @change="${this.onChangePointValue}"
                                    >
                                    </proxi-textfield>
                                </div>
                                <div class="value">
                                    <p class="slider-header">Submission Type</p>
                                    <proxi-selectfield
                                        id="submission_type"
                                        outlined
                                        class="input reward-criteria"
                                        @selected="${this.onChangeSubmissionType}"
                                        value="${this.submission_type}"
                                    >
                                        <mwc-list-item value="text">Text</mwc-list-item>
                                        <mwc-list-item value="photos">Photo(s)</mwc-list-item>
                                        <mwc-list-item value="url">Url (link)</mwc-list-item>
                                        <mwc-list-item value="receipt">Receipt</mwc-list-item>
                                    </proxi-selectfield>
                                </div>
                            </div>

                            `
                        : ''
                    }
                </span>
                <div class="edit__icons">
                    <span class="edit__icon">
                        <img
                            class="edit__icon-image-main"
                            src="${this.renderableIconString}"
                            style="transform:${this.rotatePreviewCSS}"
                        >
                    </span>
                    <span class="edit__icon"  @click="${() => { this.iconUrlSelector.open = true; this.searchForIcon();} }">
                        Change Award Icon
                    </span>
                </div>
            </span>
            <mwc-dialog
                id="iconUrlSelector"
                class="dialog edit__dialog"
                hideActions
                scrimClickAction=""
                escapeKeyAction=""
            >
                <h3>
                    Change Icon
                </h3>

                <div class="tab">
                    <span class="tablinks" @click="${async () => await this.changeOpenTab('selectIconContent')}">Select Icon</span>
                    <span class="tablinks" @click="${async () => await this.changeOpenTab('uploadContent')}">Upload Icon</span>
                </div>
                ${this.openTab === 'selectIconContent' ? html`
                    <div id="selectIconContent" class="tabcontent">
                        <mwc-textfield
                            outlined
                            id="search"
                            class="input"
                            label="Search"
                            value=${'award'}
                            @input="${this.searchForIcon}"
                        >
                        </mwc-textfield>
                        <div class="icon-options">
                            ${this.iconSearchResults.length === 0 ? html`
                                <span class="no-results">No results found, try a different search</span>
                            `: html`
                                ${this.iconSearchResults.map(
                                    (icon) => html`
                                        <mwc-button @click="${this.clickIcon.bind(this, icon)}">
                                            <img
                                                src="https://img.icons8.com/${icon.platform}/${icon.commonName}.png"
                                                width="24"
                                                height="24"/>
                                        </mwc-button>
                                    `
                                )}
                            `}
                        </div>
                    </div>
                ` : ''}
                ${this.openTab === 'uploadContent' ? html`
                    <div id="uploadContent" class="tabcontent">
                        <image-upload-component
                            autoUpload
                            .imageUrl="${this.icon}"
                            @retreived-autoupload-url="${this.onChangeIconUrl}"
                            @changed-image-url=${this.onChangeIconUrl}
                        ></image-upload-component>
                    </div>
                ` : ''}

                <div class="icon__actions">
                    <form-button variant="social" @click="${this.cancelImageUpload}">
                        Cancel
                    </form-button>
                    <form-button @click="${() => this.iconUrlSelector.close()}">Save</form-button>
                </div>
            </mwc-dialog>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .edit__inputs-container {
                display: flex;
            }

            .edit__inputs {
                width: 85%;
            }

            .edit__textfield {
                margin-top: 4px;
            }

            .tablinks {
                cursor: pointer;
                border-bottom: 1px solid #ccc;
            }

            .edit__dialog {
                --mdc-dialog-min-width: 360px;
            }

            .edit__icons {
                margin-left: 8px;
            }

            .edit__icon {
                display: flex;
                font-size: 13px;
                text-decoration: underline;
                cursor: pointer;
            }

            .edit__icon:not(.edit__tag) {
                margin-bottom: 12px;
            }

            .edit__icon.edit__tag {
                flex-direction: column;
            }

            .edit__icon-image {
                padding-right: 4px;
                --mdc-icon-size: 18px;
                --mdc-icon-button-size: 18px;
            }

            .edit__icon-image-main {
                padding-right: 4px;
                margin-left: auto;
                margin-right: auto;
                height: 32px;
                width: 32px;
            }

            .icon__actions {
                padding-top: 18px;
                display: flex;
                justify-content: space-evenly;
            }

            .success-criteria {
                width: 100%;
                display: flex;
                box-sizing: border-box;
                justify-content: space-between;
                margin: 15px 0;
                gap: 10px;
            }

            /* Style the tab */
            .tab {
                display: flex;
                justify-content: space-evenly;
                border-bottom: 1px solid #ccc;
                cursor: pointer;
                margin-bottom: 10px;
            }

            /* Style the tab content */
            .tabcontent {
            padding: 6px 12px;
            max-width: 360px;
            max-height: 360px;
            overflow-y: auto;
            }
        `;
    }

}
